import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { queryKeys } from '@/shared/api/queryKeys';
import { CountTabs } from '@/shared/components/CountTabs';
import { EmptyState } from '@/shared/components/EmptyState';
import { ErrorState } from '@/shared/components/ErrorState';
import { ListPagination } from '@/shared/components/ListPagination';
import { PageHeader } from '@/shared/components/PageHeader';
import { useListUrlState } from '@/shared/hooks/useListUrlState';
import type { Page } from '@/shared/api/page';
import { evalCasesUrl, fetchFeedback, fetchFeedbackCounts, type Feedback } from '../api/feedbackApi';
import { FeedbackSummary } from '../components/FeedbackSummary';
import { HelpfulList } from '../components/HelpfulList';
import { ReportedCard } from '../components/ReportedCard';

const DEFAULTS = { pageSize: 10, filters: { verdict: 'down' } };
const RECENT_HELPFUL = { page: 1, pageSize: 3, verdict: 'up' };

type RatingsProps = { page: Page<Feedback>; isReported: boolean; paging: { page: number; pageSize: number; setPage: (p: number) => void; setPageSize: (s: number) => void } };

function Ratings({ page, isReported, paging }: RatingsProps) {
  if (page.total === 0) return <EmptyState title={isReported ? 'No problems reported' : 'No helpful ratings yet'} hint="Feedback appears when RMs rate answers." />;
  return (
    <Stack sx={{ gap: 1.5 }}>
      {isReported ? page.items.map((f, i) => <ReportedCard key={f.id} f={f} delay={i * 50} />) : <HelpfulList items={page.items} />}
      {page.total > paging.pageSize ? (
        <ListPagination total={page.total} page={paging.page} pageSize={paging.pageSize} onPageChange={paging.setPage} onPageSizeChange={paging.setPageSize} />
      ) : null}
    </Stack>
  );
}

/** What RMs said about answers. Flags are where research corrects the assistant; the export turns them into
 * accuracy-test cases so the same mistake is caught on every later run. */
export function FeedbackPage() {
  const list = useListUrlState(DEFAULTS);
  const isReported = list.filters.verdict !== 'up';
  const query = useQuery({
    queryKey: queryKeys.feedback.list(list.params),
    queryFn: ({ signal }) => fetchFeedback(list.params, signal),
    placeholderData: keepPreviousData,
  });
  const recent = useQuery({
    queryKey: queryKeys.feedback.list(RECENT_HELPFUL),
    queryFn: ({ signal }) => fetchFeedback(RECENT_HELPFUL, signal),
    enabled: isReported,
  });
  const counts = useQuery({ queryKey: queryKeys.feedback.counts(), queryFn: ({ signal }) => fetchFeedbackCounts(signal) }).data;

  return (
    <>
      <PageHeader
        title="Answer feedback"
        description="Answers RMs rated in the portal and Teams. Export the flags as test cases for checking the answers."
        actions={<Button variant="outlined" startIcon={<Download size={15} />} href={evalCasesUrl()} sx={{ color: 'text.primary', borderColor: 'divider', bgcolor: 'background.paper' }}>Export flags as test cases</Button>}
      />
      <Stack sx={{ gap: 2.5 }}>
        {counts ? <FeedbackSummary counts={counts} /> : <Skeleton variant="rounded" height={104} />}
        <CountTabs
          label="Ratings"
          value={isReported ? 'down' : 'up'}
          onChange={(v) => { list.setFilters({ verdict: v }); }}
          options={[{ value: 'down', label: 'Reported problems', count: counts?.unreviewed }, { value: 'up', label: 'Helpful', count: counts?.up }]}
        />
        {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
        {query.data ? <Ratings page={query.data} isReported={isReported} paging={list} /> : null}
        {isReported ? <HelpfulList items={recent.data?.items ?? []} title="Recently rated helpful" /> : null}
      </Stack>
    </>
  );
}
